import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";

import { hashCode } from "../../utils";
import { useKeyboardNavigableList } from "../../hooks";

import { InputContainer2023 } from "./input-container";
import { SingleLineTextInput2023Props } from "./single-line-text-input-2023";

export interface AutocompleteInputExpects {
  currentValue: string;
  isDebouncedLoadingInProgress: boolean;
  suggestions: string[];
  hasBeenSelectedFromTheAutocomplete: boolean;
  error?: string;
  isValidatingAddressSelectionInProgress: boolean;
  required?: boolean;
}

export interface AutocompleteInputFormController {
  handleSelectASuggestion: (suggestion: string) => void;
}

export interface AutocompleteInput2023Props
  extends Omit<SingleLineTextInput2023Props, "formRef"> {
  formRef: React44ms.Api.TypedRef<AutocompleteInputExpects>;
}

export const AutocompleteInput2023: React.FunctionComponent<
  AutocompleteInput2023Props
> = (props) => {
  const [formState ] = React44ms.React.useForm(props.formRef);
  const { required } = formState || {};

  const [isFocused, setIsFocused] = React.useState(false);
  const [danglingBrowserAutofill, setDanglingBrowserAutofill] =
    React.useState(false);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const selectionRef = React.useRef<{ start: number; end: number } | null>(
    null
  );

  const name = React.useMemo(() => {
    return React44ms.React.Presentation.elements.nameFromRef(props.formRef);
  }, [props.formRef]);

  const isLoadingSuggestions = React.useMemo(() => {
    const isLoadingSuggestions = !!formState?.isDebouncedLoadingInProgress;
    return isLoadingSuggestions;
  }, [formState?.isDebouncedLoadingInProgress]);
  const suggestions = React.useMemo(() => {
    return formState?.suggestions || [];
  }, [formState?.suggestions]);
  const hasTheValueBeenSelectedFromTheSuggestions = React.useMemo(() => {
    return !!formState?.hasBeenSelectedFromTheAutocomplete;
  }, [formState?.hasBeenSelectedFromTheAutocomplete]);
  const currentError = React.useMemo(() => {
    return formState?.error;
  }, [formState?.error]);
  const isDisabled = React.useMemo(() => {
    const isValidatingTheAddressSelection =
      !!formState?.isValidatingAddressSelectionInProgress;
    return isValidatingTheAddressSelection;
  }, [formState?.isValidatingAddressSelectionInProgress]);
  const value = React.useMemo(() => {
    if (formState?.currentValue && typeof formState?.currentValue !== 'string') {
      return '';
    }

    return formState?.currentValue || "";
  }, [formState?.currentValue]);

  const suggestionsDropdownMode = React.useMemo(() => {
    let suggestionsDropdownMode:
      | "nothing"
      | "show-suggestions"
      | "show-loading"
      | "show-empty" = "nothing";
    if (suggestions.length > 0) {
      suggestionsDropdownMode = "show-suggestions";
    } else if (isLoadingSuggestions) {
      suggestionsDropdownMode = "show-loading";
    } else if (value.length > 0) {
      suggestionsDropdownMode = "show-empty";
    }
    return suggestionsDropdownMode;
  }, [suggestions, isLoadingSuggestions, value]);
  const isDropdownOpen = React.useMemo(
    () =>
      !isDisabled && isFocused && !hasTheValueBeenSelectedFromTheSuggestions,
    [isDisabled, isFocused, hasTheValueBeenSelectedFromTheSuggestions]
  );

  const handleSuggestionChosen = React.useCallback(
    (suggestion: string) => {
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        [
          props.formRef,
          React44ms.Api.YieldablePort(
            React44ms.Api.Convenience.element.name<AutocompleteInputFormController>(
              "handleSelectASuggestion"
            )
          ),
        ],
        suggestion
      );
      inputRef.current?.blur();
    },
    [props.formRef]
  );

  const [activeSuggestionIndex, setActiveSuggestionIndex] =
    useKeyboardNavigableList({
      myList: suggestions,
      canInteract: isFocused,
      handleSelect: handleSuggestionChosen,
    });

  const handleChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      async (event) => {
        selectionRef.current = {
          start: event.target.selectionStart || 0,
          end: event.target.selectionEnd || 0,
        };
        /**
         * If autofill is being used it will
         * trigger change without this field having focus.
         */
        if (
          inputRef?.current &&
          document.activeElement &&
          document.activeElement === inputRef?.current
        ) {
          // when it's being filled with focus
          React44ms.React.Event.dispatch(
            React44ms.React.Convenient.input.onChange,
            [props.formRef],
            event.target.value
          );
        } else {
          // when it's being filled by autofill initiated from another field
          setDanglingBrowserAutofill(true);
          const newValueFromBrowserAutofill = `${event.target.value}`;
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
            React44ms.React.Event.dispatch(
              React44ms.React.Convenient.input.onChange,
              [props.formRef],
              newValueFromBrowserAutofill
            );
            setDanglingBrowserAutofill(false);
          }, 1500);
        }
      },
      [props.formRef, setDanglingBrowserAutofill]
    );
  React.useLayoutEffect(() => {
    const input = inputRef.current;
    const selection = selectionRef.current;
    if (!input || !selection) {
      return;
    }
    if (document.activeElement !== input) {
      return;
    }
    const max = input.value.length;
    const start = Math.min(selection.start, max);
    const end = Math.min(selection.end, max);
    input.setSelectionRange(start, end);
  }, [value]);
  const handleFocus: React.FocusEventHandler<HTMLInputElement> =
    React.useCallback(
      (event) => {
        setIsFocused(true);
        if (inputRef.current) {
          // turn off autocomplete while focused
          inputRef.current.setAttribute(
            "autocomplete",
            "bbnt_custom_autocomplete_input_no_autocomplete_on_focus"
          );
        }

        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onFocus,
          [props.formRef],
          null
        );
      },
      [props.formRef]
    );
  const handleBlur: React.FocusEventHandler<HTMLInputElement> =
    React.useCallback(
      (event) => {
        setIsFocused(false);
        if (inputRef.current) {
          inputRef.current.removeAttribute("autocomplete");
        }

        if (!danglingBrowserAutofill) {
          React44ms.React.Event.dispatch(
            React44ms.React.Convenient.input.onBlur,
            [props.formRef],
            null
          );
        }
      },
      [props.formRef, danglingBrowserAutofill]
    );

  const onFocusedDecorationClick: React.MouseEventHandler<HTMLDivElement> =
    React.useCallback(() => {
      if (!isDisabled && inputRef.current) {
        inputRef.current.blur();
      }
    }, [isDisabled]);

  if (!name) {
    return null;
  }

  return (
    <InputContainer2023
      {...props}
      name={name}
      errorValueIfAny={currentError}
      required={!!required}
      className={`autocomplete-input ${props?.className || ""}`}
    >
      <div
        className={`input-immediate-container ${
          suggestionsDropdownMode === "nothing"
            ? "no-suggestion-dropdown-yet"
            : "has-sometype-of-suggestions-dropdown"
        } ${isLoadingSuggestions ? "loading-suggestions" : ""} ${
          hasTheValueBeenSelectedFromTheSuggestions
            ? "validated"
            : isFocused
            ? "focused"
            : currentError
            ? "error"
            : ""
        } ${isDisabled ? "pretend-disabled" : ""}`}
      >
        <input
          name={name}
          ref={inputRef}
          value={value}
          disabled={isDisabled}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={props.placeholder}
          title={props.title}
          autoComplete={props.autoComplete}
          type={props.type ? props.type : "text"}
          required={!!required}
        ></input>

        <div
          className="decoration-container"
          onClick={isFocused ? onFocusedDecorationClick : undefined}
        ></div>

        <div className="loading-decoration-container">
          <LoadingSpinner />
        </div>

        <div
          className={`suggestions-container ${
            isDropdownOpen && suggestionsDropdownMode !== "nothing"
              ? "show"
              : ""
          }`}
        >
          {suggestionsDropdownMode === "show-suggestions" ? (
            <React.Fragment>
              <div className="keep-going">
                Keep typing your address to refine results
              </div>
              {suggestions.map((suggestion, index) => {
                const onHoverEnter = () => {
                  setActiveSuggestionIndex(index);
                };

                const onClick: React.MouseEventHandler<HTMLDivElement> = (
                  event
                ) => {
                  event.preventDefault();
                  handleSuggestionChosen(suggestion);
                };

                // need to prevent the div from acquiring focus on mouse down
                const onMouseDown: React.MouseEventHandler<HTMLDivElement> = (
                  event
                ) => {
                  event.preventDefault();
                };

                return (
                  <div
                    key={hashCode(suggestion)}
                    className={`suggestion ${
                      index === activeSuggestionIndex ? "active" : ""
                    }`}
                    onMouseEnter={onHoverEnter}
                    onMouseDown={onMouseDown}
                    onClick={onClick}
                  >
                    {suggestion}
                  </div>
                );
              })}
            </React.Fragment>
          ) : suggestionsDropdownMode === "show-loading" ? (
            <div className="react-select-no-option loading-state">
              Searching for {value}...
            </div>
          ) : suggestionsDropdownMode === "show-empty" ? (
            <div className="blank-state">
              <span>{`No address matching your search.`}</span>
              <hr />
              <span>{`Keep typing your address to refine results`}</span>
            </div>
          ) : null}
        </div>

        {props?.children}
      </div>
    </InputContainer2023>
  );
};

const LoadingSpinner: React.FunctionComponent = () => {
  return (
    <div className="ispinner">
      <div className="bounce1"></div>
      <div className="bounce2"></div>
      <div className="bounce3"></div>
    </div>
  );
};
